import * as React from "react";
import { useResource, Resources } from "Translations/Resources";
import { useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import {
  getAccessRoleDetailAsync,
  updateRoleAccessRightsAsync,
} from "State/AccessRoles/AccessRolesReducer";
import { RootStateType } from "State/Store";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { AccessRoleAccessRights } from "Components/Settings/AccessRoles/AccessRoleAccessRights";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { AccessRoleAccessRightDto } from "Api/Api";
import { setDisableRedirect } from "State/App/AppReducer";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";

type RouteProps = {
  id: string;
};

export const AccessRoleDetailPage: React.FunctionComponent = _ => {
  const { t } = useResource();
  const { params } = useRouteMatch<RouteProps>();
  const accessRoleID = params.id as any;
  const dispatch = useDispatch();
  const { accessRoleDetail, isLoading } = useSelector(
    (e: RootStateType) => e.accessRoles,
  );
  const [accessRights, setAccessRights] = React.useState<
    AccessRoleAccessRightDto[]
  >([]);

  React.useEffect(() => {
    dispatch(getAccessRoleDetailAsync.request(accessRoleID));
  }, [dispatch, accessRoleID]);

  React.useEffect(() => {
    if (accessRoleDetail?.accessRights) {
      setAccessRights(accessRoleDetail.accessRights);
    }
  }, [accessRoleDetail]);

  const save = () => {
    if (accessRoleDetail) {
      dispatch(
        updateRoleAccessRightsAsync.request({
          accessRoleID: accessRoleDetail.accessRoleID,
          accessRightsCodes: accessRights
            .filter(e => e.isSelected)
            .map(e => e.accessRightCode),
        }),
      );
      dispatch(setDisableRedirect(false));
    }
  };

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.AccessRoles.Detail.Title),
        url: t(Resources.Routes.AccessRoles),
      } as BreadcrumbItem,
      {
        name: t(Resources.AccessRoles.Detail.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  return (
    <AuthenticatedLayout
      title={accessRoleDetail?.name ?? ""}
      breadcrumbItems={getBreadcrumbItems()}
    >
      <div className={"container-small"}>
        <div className={"box detail"}>
          <div className={"box-header"}>
            {!isLoading && accessRoleDetail ? (
              t(Resources.Users.Detail.Title)
            ) : (
              <PrimarySkeleton height={30} />
            )}
          </div>
          <div className={"box-content"}>
            {!isLoading && accessRoleDetail ? (
              <>
                <AccessRoleAccessRights
                  accessRights={accessRights}
                  setAccessRights={setAccessRights}
                />

                <PrimaryButton onClick={save}>
                  {t(Resources.AccessRoles.Detail.Save)}
                </PrimaryButton>
              </>
            ) : (
              <>
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
              </>
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};
